import { useState, useEffect, useCallback } from "react";
import {
    Gift, Loader2, IndianRupee, Package, Percent, CheckCircle2, XCircle,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMER_REFERRAL_REWARDS_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { formatDate } from "../../utils/datetime";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const TYPE_META = {
    money: { label: "Money", Icon: IndianRupee },
    amc_discount: { label: "AMC discount", Icon: Percent },
    free_modules: { label: "Free modules", Icon: Package },
};

const STATUS_META = {
    approved: { label: "Approved", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    settled: { label: "Settled", cls: "bg-green-100 text-green-800 border-green-200" },
    cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700 border-red-200" },
};

/** What this customer has earned for referring other hospitals.
 *
 *  Read-only here by design, though the decision is made in this system: an
 *  award only makes sense next to the referrals it covers and the running
 *  totals, which live on the Referral Rewards screen. This panel answers the
 *  narrower question a rep has while looking at one customer.
 *
 *  Drafts are shown — unlike the copy pushed to eMedHub, which starts at
 *  approved. Internally, "we are considering this" is worth seeing.
 */
export default function ReferralBenefitsPanel({ customerId }) {
    const { fetch } = useApiMethods();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(CUSTOMER_REFERRAL_REWARDS_URL, { customer_id: customerId })
            .then(res => {
                if (res?.status === "success") setRows(res.data || []);
                else ToastMsg("error", res?.message || "Could not load referral rewards");
            })
            .catch(() => ToastMsg("error", "Could not load referral rewards"))
            .finally(() => setLoading(false));
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading referral rewards…
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <Gift className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-600">No referral rewards yet.</p>
                <p className="text-xs text-gray-400 mt-1">
                    Grant one from the Referral Rewards screen.
                </p>
            </div>
        );
    }

    // Only what has actually been granted counts toward the total. A cancelled
    // award is kept visible for the audit trail but must not inflate a figure
    // somebody might quote back to the customer.
    const live = rows.filter(r => r.status !== "cancelled");
    const total = live.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const outstanding = live
        .filter(r => r.status !== "settled")
        .reduce((sum, r) => sum + Number(r.amount || 0), 0);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs text-gray-500">Total awarded</p>
                    <p className="text-lg font-semibold text-gray-900">{money(total)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs text-gray-500">Not yet settled</p>
                    <p className={cn("text-lg font-semibold",
                        outstanding > 0 ? "text-amber-700" : "text-gray-900")}>
                        {money(outstanding)}
                    </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs text-gray-500">Awards</p>
                    <p className="text-lg font-semibold text-gray-900">{rows.length}</p>
                </div>
            </div>

            <div className="space-y-3">
                {rows.map((r) => {
                    const type = TYPE_META[r.benefit_type] || { label: r.benefit_type, Icon: Gift };
                    const st = STATUS_META[r.status]
                        || { label: r.status, cls: "bg-gray-100 text-gray-600 border-gray-200" };
                    const TypeIcon = type.Icon;
                    return (
                        <div
                            key={r.id}
                            className={cn(
                                "rounded-lg border bg-white px-4 py-3",
                                r.status === "cancelled"
                                    ? "border-gray-200 opacity-60"
                                    : "border-gray-200",
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <TypeIcon className="w-4 h-4 text-gray-500 shrink-0" />
                                    <span className="text-sm font-medium text-gray-900">
                                        {type.label}
                                    </span>
                                    <span className="text-sm text-gray-900">{money(r.amount)}</span>
                                </div>
                                <span className={cn(
                                    "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0",
                                    st.cls,
                                )}>
                                    {st.label}
                                </span>
                            </div>

                            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                                {r.description}
                            </p>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                                <span>
                                    {r.beneficiary_type === "individual"
                                        ? `To ${r.beneficiary_name || "an individual"}`
                                        : "To the hospital"}
                                </span>
                                {r.referral_codes && (
                                    <span className="font-mono">{r.referral_codes}</span>
                                )}
                                {r.approved_at && <span>Approved {formatDate(r.approved_at)}</span>}
                                {r.settled_at && <span>Settled {formatDate(r.settled_at)}</span>}
                            </div>

                            {r.settlement_note && (
                                <p className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                                    <span>{r.settlement_note}</span>
                                </p>
                            )}

                            {r.status === "cancelled" && (
                                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                                    <XCircle className="w-3.5 h-3.5" />
                                    Cancelled in eMedHub — kept here for the record.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
